import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  Briefcase,
  Clock,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Menu as MenuIcon,
  MessageCircle,
  Phone,
  Twitter,
} from "lucide-react";
import { motion } from "framer-motion";
import ContactFab, { ContactModal } from "@/components/ContactFab";
import BrandMark from "@/components/layout/BrandMark";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
});

const contactCards = [
  { icon: <MapPin />, label: "Visit Us", title: "Victoria House, 4th Floor", text: "Sayansi, Dar es Salaam, Tanzania" },
  { icon: <Mail />, label: "Email Us", title: "info@taskpart.co.tz", text: "partners@taskpart.co.tz" },
  { icon: <Phone />, label: "Call Us", title: "+255 784 650 219", text: "Mon to Fri, 08:00 - 17:00 EAT" },
  { icon: <Clock />, label: "Response Time", title: "Within 24 hours", text: "Standard business inquiries" },
];

const navLinks = [
  { label: "Home", to: "/" as const },
  { label: "Sign in", to: "/auth/login" as const },
];

const socials = [
  { Icon: Facebook, label: "Facebook" },
  { Icon: Instagram, label: "Instagram" },
  { Icon: Twitter, label: "Twitter" },
  { Icon: Linkedin, label: "LinkedIn" },
];

const footerLink = "text-sm text-body transition-colors hover:text-foreground";

function ContactPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 md:px-6">
          <Link
            to="/"
            aria-label="Taskpart home"
            className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <BrandMark />
          </Link>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <nav aria-label="Primary" className="hidden items-center gap-0.5 md:flex">
              {navLinks.map((link) => (
                <Button key={link.label} variant="ghost" size="sm" asChild>
                  <Link to={link.to}>{link.label}</Link>
                </Button>
              ))}
            </nav>
            <Button size="sm" onClick={() => setContactOpen(true)}>
              Get in touch
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              className="md:hidden"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              <MenuIcon />
            </Button>
          </div>
        </div>
      </header>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="right" className="flex w-[84vw] flex-col gap-0 p-0 sm:max-w-[340px]">
          <div className="flex h-16 items-center border-b border-border px-5">
            <BrandMark />
          </div>
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SheetDescription className="sr-only">Site navigation</SheetDescription>
          <nav aria-label="Mobile" className="flex flex-col gap-0.5 p-3">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className="rounded-sm px-3 py-2.5 text-body-md text-foreground hover:bg-accent"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </SheetContent>
      </Sheet>

      <main>
        {/* HERO */}
        <section className="mx-auto max-w-6xl px-4 pt-14 pb-12 text-center md:px-6 md:pt-24 md:pb-16">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="eyebrow mb-5">Get in touch</p>
            <h1 className="mx-auto max-w-3xl text-[36px] leading-[42px] font-normal tracking-[-1px] text-foreground sm:text-display-lg sm:leading-[52.8px] sm:tracking-[-1.2px] lg:text-display-xl lg:leading-[70.4px] lg:tracking-[-1.6px]">
              Let&apos;s talk about your{" "}
              <span className="font-serif italic">placement program</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-body-md text-body md:text-body-lg">
              Reach our operations team for onboarding guidance, institutional partnership conversations, technical support, or product inquiries.
            </p>
          </motion.div>
        </section>

        {/* CONTACT INFO CARDS */}
        <section className="mx-auto max-w-6xl px-4 pb-16 md:px-6 md:pb-24">
          <ul className="grid gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {contactCards.map((card, i) => (
              <motion.li
                key={card.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-card p-5 md:p-6"
              >
                <div className="mb-5 flex size-9 items-center justify-center rounded-sm border border-border bg-background text-foreground [&_svg]:size-4">
                  {card.icon}
                </div>
                <p className="eyebrow mb-1.5">{card.label}</p>
                <p className="text-body-md font-medium break-words text-foreground">{card.title}</p>
                <p className="mt-1 text-sm text-body">{card.text}</p>
              </motion.li>
            ))}
          </ul>
        </section>

        {/* MAP + INFO + CTA */}
        <section className="border-t border-border">
          <div className="mx-auto grid max-w-6xl gap-4 px-4 py-16 md:px-6 md:py-24 lg:grid-cols-[1.05fr_0.95fr] lg:gap-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45 }}
              className="min-h-[280px] overflow-hidden rounded-md border border-border bg-card md:min-h-[460px]"
            >
              <iframe
                title="Taskpart office"
                src="https://www.google.com/maps?q=Victoria%20House%2C%20Sayansi%2C%20Dar%20es%20Salaam&z=15&output=embed"
                className="block h-full min-h-[280px] w-full border-0 md:min-h-[460px]"
                loading="lazy"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: 0.08 }}
              className="flex flex-col gap-4"
            >
              <div className="rounded-md border border-border bg-card p-6 md:p-8">
                <BrandMark showWordmark={false} />
                <h2 className="mt-6 text-display-sm font-normal text-foreground">
                  Prefer a <span className="font-serif italic">quick</span> chat?
                </h2>
                <p className="mt-3 text-sm leading-6 text-body">
                  Skip the form. Open a guided conversation with our operations team in seconds.
                </p>
                <Button size="lg" className="mt-6" onClick={() => setContactOpen(true)}>
                  <MessageCircle />
                  Start the conversation
                </Button>
              </div>

              <div className="rounded-md border border-border bg-card p-6">
                <p className="eyebrow mb-4">Recommended contact path</p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-foreground">
                      <Briefcase className="size-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">Institutions &amp; employers</p>
                      <a
                        href="mailto:partners@taskpart.co.tz"
                        className="font-mono text-code break-words text-body hover:text-foreground"
                      >
                        partners@taskpart.co.tz
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-foreground">
                      <Mail className="size-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">General &amp; product</p>
                      <a
                        href="mailto:info@taskpart.co.tz"
                        className="font-mono text-code break-words text-body hover:text-foreground"
                      >
                        info@taskpart.co.tz
                      </a>
                    </div>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 pt-14 pb-8 md:px-6 md:pt-16">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr]">
            <div>
              <BrandMark />
              <p className="mt-4 max-w-[320px] text-sm leading-6 text-body">
                Smart placement operations for a more connected and resilient education-to-industry pipeline.
              </p>
              <div className="mt-5 flex gap-1.5">
                {socials.map(({ Icon, label }) => (
                  <Button key={label} variant="outline" size="icon-sm" aria-label={label}>
                    <Icon />
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <h3 className="eyebrow mb-4">Quick links</h3>
              <ul className="space-y-2.5">
                <li><Link to="/" className={footerLink}>Home</Link></li>
                <li><Link to="/auth/login" className={footerLink}>Sign in</Link></li>
                <li><Link to="/auth/register" className={footerLink}>Create account</Link></li>
                <li><Link to="/onboarding/partner" className={footerLink}>Partner onboarding</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="eyebrow mb-4">Contact</h3>
              <ul className="space-y-2.5">
                <li className="text-sm leading-6 text-body">
                  Victoria House, 4th Floor
                  <br />
                  Sayansi, Dar es Salaam
                </li>
                <li><a href="mailto:info@taskpart.co.tz" className={footerLink}>info@taskpart.co.tz</a></li>
                <li><a href="mailto:partners@taskpart.co.tz" className={footerLink}>partners@taskpart.co.tz</a></li>
                <li className="text-sm text-body tabular-nums">+255 784 650 219</li>
              </ul>
            </div>
          </div>
          <div className="mt-12 flex flex-col gap-3 border-t border-border pt-6 md:flex-row md:items-center md:justify-between">
            <p className="text-caption text-muted-foreground">
              © 2026 Taskpart Connect. Designed for professional placement operations.
            </p>
            <div className="flex gap-5">
              {["Privacy", "Terms", "Cookies"].map((t) => (
                <a key={t} href="#" className="text-caption text-muted-foreground transition-colors hover:text-foreground">
                  {t}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
      <ContactFab />
    </div>
  );
}
